import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import DatePicker from '../../Fundamentals/DatePicker';
import FormField from '../../Fundamentals/Form/FormField';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import dateUtils from '../../../Utils/dateUtils';
import type { PackageSalesChannel, SalesChannel, UpdatePackageSalesChannelRequest } from './Packages.types';

interface PackageSalesChannelDrawerProps {
    channel?: PackageSalesChannel;
    availableChannels: SalesChannel[] | null | undefined;
    onClose: () => void;
    onSave: (salesChannelId: string, validity: UpdatePackageSalesChannelRequest) => void;
    isSaving: boolean;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const toDateText = (value: string | null | undefined): string => (value === null || value === undefined ? '' : dateUtils.formatDate(value));

const toValidFromRequest = (text: string): string | null => (text.length === 0 ? null : text);

// VOSS treats validTo as an exact moment, so the end of the day keeps the chosen day included, as the status badge assumes
const toValidToRequest = (text: string): string | null => (text.length === 0 ? null : `${text}T23:59:59`);

// an untouched field goes back exactly as stored, so editing one date doesn't round the other to midnight
const keepIfUntouched = (text: string, stored: string | null | undefined, toRequest: (text: string) => string | null): string | null =>
    text === toDateText(stored) ? (stored ?? null) : toRequest(text);

const PackageSalesChannelDrawer = ({ channel, availableChannels, onClose, onSave, isSaving }: PackageSalesChannelDrawerProps) => {
    const { t } = useTranslation();

    const [salesChannelId, setSalesChannelId] = useState(channel?.id ?? '');
    const [validFrom, setValidFrom] = useState(() => toDateText(channel?.validFrom));
    const [validTo, setValidTo] = useState(() => toDateText(channel?.validTo));
    const [isValidFromParsed, setIsValidFromParsed] = useState(true);
    const [isValidToParsed, setIsValidToParsed] = useState(true);

    const isEdit = channel !== undefined;
    // both are YYYY-MM-DD, so string order is calendar order
    const rangeInvalid = validFrom.length > 0 && validTo.length > 0 && validTo < validFrom;
    const canSave = !isSaving && salesChannelId.length > 0 && isValidFromParsed && isValidToParsed && !rangeInvalid;

    const sortedChannels = [...(availableChannels ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'sv'));

    const placeholderKey = () => {
        if (availableChannels === undefined) return 'packages.loadingSalesChannels';
        if (availableChannels === null) return 'packages.salesChannelsNotLoaded';
        return sortedChannels.length === 0 ? 'packages.allChannelsLinked' : 'packages.selectChannel';
    };

    return (
        <Drawer
            title={(isEdit ? t('packages.editSalesChannelTitle', { name: channel.name }) : t('packages.addSalesChannelTitle')) ?? ''}
            subtitle={t(isEdit ? 'packages.editSalesChannelSubtitle' : 'packages.addSalesChannelSubtitle') ?? ''}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                    <Button disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        option={ButtonOption.PRIMARY}
                        disabled={!canSave}
                        onClick={() =>
                            onSave(salesChannelId, {
                                validFrom: keepIfUntouched(validFrom, channel?.validFrom, toValidFromRequest),
                                validTo: keepIfUntouched(validTo, channel?.validTo, toValidToRequest),
                            })
                        }
                    >
                        {t(isEdit ? 'common.save' : 'common.add')}
                    </Button>
                </>
            }
        >
            <FormField label={t('packages.salesChannel') ?? ''} htmlFor="package-sales-channel" required help={isEdit ? undefined : (t('packages.selectChannelHelp') ?? '')}>
                <select
                    id="package-sales-channel"
                    className="form-control"
                    value={salesChannelId}
                    disabled={isEdit || sortedChannels.length === 0}
                    onChange={(e) => setSalesChannelId(e.target.value)}
                >
                    {isEdit ? (
                        <option value={salesChannelId}>{channel.name}</option>
                    ) : (
                        <>
                            <option value="">{t(placeholderKey())}</option>
                            {sortedChannels.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </>
                    )}
                </select>
            </FormField>

            <div className="d-flex flex-wrap gap-16">
                <div className="flex-1">
                    <DatePicker
                        label={t('packages.validFrom')}
                        initialValue={validFrom}
                        dateFormat={DATE_FORMAT}
                        showTime={false}
                        isValidDate={(day) => validTo.length === 0 || !day.isAfter(validTo, 'day')}
                        onChange={setValidFrom}
                        onValidityChange={setIsValidFromParsed}
                    />
                    <span className="help-block font-size-xs">{t('packages.optional')}</span>
                </div>
                <div className="flex-1">
                    <DatePicker
                        label={t('packages.validTo')}
                        initialValue={validTo}
                        dateFormat={DATE_FORMAT}
                        showTime={false}
                        error={rangeInvalid ? t('packages.validToBeforeValidFrom') : undefined}
                        isValidDate={(day) => validFrom.length === 0 || !day.isBefore(validFrom, 'day')}
                        onChange={setValidTo}
                        onValidityChange={setIsValidToParsed}
                    />
                    <span className="help-block font-size-xs">{t('packages.optional')}</span>
                </div>
            </div>
        </Drawer>
    );
};

export default PackageSalesChannelDrawer;
